import React, { createContext, useMemo } from 'react';

import {
  GetCreditsInterestFormRequest,
  GetCreditsInterestFormResponse,
  UpsertCreditsInterestFormRequest,
  UpsertCreditsInterestFormResponse,
  GetBillingAddressRequest,
  GetBillingAddressResponse,
  UpsertBillingAddressRequest,
  UpsertBillingAddressResponse,
  GetInvoicingAccountRequest,
  GetInvoicingAccountResponse,
  GetOrganizationInformationRequest,
  GetOrganizationInformationResponse,
  GetPrimaryContactInformationRequest,
  GetPrimaryContactInformationResponse,
  GetUserRightsRequest,
  GetUserRightsResponse,
  FormUser,
  FormQuestions,
  AsxAddress,
  SaveCreditsInterestFormRequest,
  SaveCreditsInterestFormResponse,
  GetUserPreferencesRequest,
  GetUserPreferencesResponse,
} from '@amzn/coral_com-amazon-asxcustomerprofileservice';

import { acpsClientSingleton as acpsClient } from './acps-client';

const CLIENT_NOT_DEFINED_MESSAGE = 'ACPS client is not defined';

const getCreditsInterestForm = async (): Promise<GetCreditsInterestFormResponse> => {
  // temporary ignore type until we add Header interceptor
  // https://sim.amazon.com/issues/ASX-1075
  const request = GetCreditsInterestFormRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getCreditsInterestForm(request).toPromise();
};

const upsertCreditsInterestForm = async (
  user: FormUser,
  questions: FormQuestions
): Promise<UpsertCreditsInterestFormResponse> => {
  const request = UpsertCreditsInterestFormRequest.clone({
    user,
    questions,
  });

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.upsertCreditsInterestForm(request).toPromise();
};

const saveCreditsInterestForm = async (
  user: FormUser,
  questions: FormQuestions
): Promise<SaveCreditsInterestFormResponse> => {
  const request = SaveCreditsInterestFormRequest.clone({
    user,
    questions,
  });

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.saveCreditsInterestForm(request).toPromise();
};

const getBillingAddress = async (): Promise<GetBillingAddressResponse> => {
  const request = GetBillingAddressRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getBillingAddress(request).toPromise();
};

const upsertBillingAddress = async (billingAddressItem: AsxAddress): Promise<UpsertBillingAddressResponse> => {
  const request = UpsertBillingAddressRequest.clone({
    billingAddressItem: AsxAddress.clone({
      ...billingAddressItem,
    }),
  });

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.upsertBillingAddress(request).toPromise();
};

const getInvoicingAccount = async (): Promise<GetInvoicingAccountResponse> => {
  const request = GetInvoicingAccountRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getInvoicingAccount(request).toPromise();
};

const getPrimaryContactInformation = async (): Promise<GetPrimaryContactInformationResponse> => {
  const request = GetPrimaryContactInformationRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getPrimaryContactInformation(request).toPromise();
};

const getOrganizationInformation = async (): Promise<GetOrganizationInformationResponse> => {
  const request = GetOrganizationInformationRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getOrganizationInformation(request).toPromise();
};

const getUserRights = async (): Promise<GetUserRightsResponse> => {
  const request = GetUserRightsRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getUserRights(request).toPromise();
};

const getUserPreferences = async (): Promise<GetUserPreferencesResponse> => {
  const request = GetUserPreferencesRequest.clone({});

  if (!acpsClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return acpsClient.getUserPreferences(request).toPromise();
};

export interface AcpsCreditsContext {
  getCreditsInterestForm: () => Promise<GetCreditsInterestFormResponse>;
  upsertCreditsInterestForm: (user: FormUser, questions: FormQuestions) => Promise<UpsertCreditsInterestFormResponse>;
  saveCreditsInterestForm: (user: FormUser, questions: FormQuestions) => Promise<SaveCreditsInterestFormResponse>;
  getBillingAddress: () => Promise<GetBillingAddressResponse>;
  upsertBillingAddress: (billingAddressItem: AsxAddress) => Promise<UpsertBillingAddressResponse>;
  getInvoicingAccount: () => Promise<GetInvoicingAccountResponse>;
  getOrganizationInformation: () => Promise<GetOrganizationInformationResponse>;
  getPrimaryContactInformation: () => Promise<GetPrimaryContactInformationResponse>;
  getUserRights: () => Promise<GetUserRightsResponse>;
  getUserPreferences: () => Promise<GetUserPreferencesResponse>;
}

export const AcpsCreditsClientContext = createContext<AcpsCreditsContext | null>(null);

export const AcpsCreditsClientProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useMemo<AcpsCreditsContext>(
    () => ({
      getCreditsInterestForm,
      upsertCreditsInterestForm,
      saveCreditsInterestForm,
      getBillingAddress,
      upsertBillingAddress,
      getInvoicingAccount,
      getOrganizationInformation,
      getPrimaryContactInformation,
      getUserRights,
      getUserPreferences,
    }),
    []
  );
  return <AcpsCreditsClientContext.Provider value={contextValue}>{children}</AcpsCreditsClientContext.Provider>;
};
